import KeyboardIcon from '@mui/icons-material/Keyboard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Stack } from '@mui/material';
import fuzzy from 'fuzzy';
import React, { useCallback, useMemo } from 'react';
import { sortBy } from 'remeda';
import { SearchDialog } from 'src/components/SearchDialog/search-dialog';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useScreenDimensions } from 'src/utils/system';
import { QuickAction } from './quick-actions-context';

export type QuickActionsModalProps = {
  isDisabled?: boolean;
  actions: QuickAction[];
};

export const QuickActionsModal: React.FC = () => {
  const { isMobile } = useScreenDimensions();
  const { modalProps } = useModalState(Modals.QuickActionsModal);
  const { closeModal } = useModalMethods(Modals.QuickActionsModal);
  const { showModal: showKeyboardShortcutsModal } = useModalMethods(Modals.ShortcutsModal);
  const { showModal: showContactSupportModal } = useModalMethods(Modals.ContactSupportModal);

  const { actions } = modalProps!;

  const filterHandler = useCallback((_: string, search = '', keywords: string[] = []) => {
    const matches = fuzzy.filter(search, keywords);
    return matches.length > 0 ? 1 : 0;
  }, []);

  const onItemSelect = useCallback(
    (item: QuickAction) => {
      closeModal();
      item.handler?.();
    },
    [closeModal],
  );

  const openKeyboardShortcutsModal = useCallback(() => {
    showKeyboardShortcutsModal();
  }, [showKeyboardShortcutsModal]);

  const openContactSupportModal = useCallback(() => {
    showContactSupportModal();
  }, [showContactSupportModal]);

  const helpAndSupportActions = useMemo<QuickAction[]>(
    () => [
      {
        id: 'keyboard-shortcuts',
        title: 'Keyboard Shortcuts',
        description: 'Browse the list of available keyboard shortcuts.',
        icon: (
          <Stack direction="row" alignItems="center" sx={{ marginRight: '1rem' }}>
            <KeyboardIcon color="primary" fontSize="small" />
          </Stack>
        ),
        handler: openKeyboardShortcutsModal,
        keywords: [
          'Keyboard Shortcuts',
          'Browse the list of available keyboard shortcuts.',
          'Help & Support',
        ],
        group: 'Help & Support',
        order: Number.MAX_SAFE_INTEGER,
      },
      {
        id: 'contact-support',
        title: 'Contact Support',
        description: 'Reach out to our support team for additional help.',
        icon: (
          <Stack direction="row" alignItems="center" sx={{ marginRight: '1rem' }}>
            <SupportAgentIcon color="primary" fontSize="small" />
          </Stack>
        ),
        handler: openContactSupportModal,
        keywords: [
          'Contact Support',
          'Reach out to our support team for additional help.',
          'Help & Support',
        ],
        group: 'Help & Support',
        order: Number.MAX_SAFE_INTEGER,
      },
    ],
    [openKeyboardShortcutsModal, openContactSupportModal],
  );

  const orderedActions = useMemo(() => {
    return sortBy([...actions, ...helpAndSupportActions], (action) => action.order ?? 999);
  }, [actions, helpAndSupportActions]);

  return (
    <SearchDialog
      options={orderedActions}
      onClose={closeModal}
      placeholder="Type to filter..."
      onSelect={onItemSelect}
      filterHandler={filterHandler}
      {...(!isMobile && { endAdornment: false })}
    />
  );
};
